/**
 * React Query hooks for white-label branding (XC-ERP1-U11 §3.5).
 *
 * - ``useTenantBranding``: the tenant's branding read, fresh for 5 minutes and
 *   refetched every 45, well inside the signed URL's 60.
 * - ``useHeaderLogo``: what the header slot paints, with the no-flash cache
 *   (``lib/brandingCache``): the cached answer paints at once, a cold cache
 *   shows a neutral placeholder (never Wingo-then-swap), and a failed read
 *   falls back to the Wingo logo.
 * - ``useCustomLogoLive``: whether a tenant logo is live (sidebar attribution).
 * - Preview / save / restore mutations; save and restore put the returned
 *   branding in the cache, so the saver's header updates at once. They are
 *   non-idempotent POSTs with no idempotency key, so they never retry (the app
 *   client's mutation default is retry: 1).
 *
 * Every key (the query, the localStorage cache) is the tenant the JWT binds the
 * request to (``currentUser.tenantId``), never the overridable
 * ``currentTenant``: the gateway reads and writes the JWT's tenant, so a key
 * from anywhere else could file one tenant's logo under another's name.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { useTenantContext } from '@/contexts/TenantContext';
import {
  readBrandingCache,
  writeBrandingCache,
  type BrandingCacheEntry,
  type BrandingCacheIndex,
} from '@/lib/brandingCache';
import {
  fetchLogoAsDataUrl,
  fetchTenantBranding,
  previewLogo,
  restoreBranding,
  saveLogo,
  type RestoreBrandingRequest,
  type SaveLogoRequest,
  type TenantBranding,
} from '@/services/brandingService';

export const BRANDING_STALE_TIME_MS = 5 * 60 * 1000;
export const BRANDING_REFETCH_INTERVAL_MS = 45 * 60 * 1000;

export const tenantBrandingKeys = {
  all: ['tenantBranding'] as const,
  tenant: (tenantId: string) => ['tenantBranding', tenantId] as const,
};

/** The tenant the branding calls act on: the JWT's (TenantContext's ``currentUser``). */
export function useBrandingTenantId(): string {
  const { currentUser } = useTenantContext();
  return currentUser.tenantId;
}

export function useTenantBranding() {
  const tenantId = useBrandingTenantId();
  return useQuery<TenantBranding>({
    queryKey: tenantBrandingKeys.tenant(tenantId),
    queryFn: fetchTenantBranding,
    enabled: !!tenantId,
    staleTime: BRANDING_STALE_TIME_MS,
    refetchInterval: BRANDING_REFETCH_INTERVAL_MS,
    retry: 1,
  });
}

/** A tenant logo is live: entitled, the current version is a logo, and it has a URL. */
export function isCustomLogoLive(b: TenantBranding | undefined | null): b is TenantBranding & {
  logo_url: string;
} {
  return !!b && b.entitled && b.kind === 'logo' && !!b.logo_url;
}

const cacheShowsLogo = (c: BrandingCacheEntry | null) =>
  !!c && c.entitled && c.kind === 'logo' && c.version !== null;

export type HeaderLogoState =
  | { kind: 'pending' }
  | { kind: 'wingo' }
  | { kind: 'custom'; src: string; badge: boolean; alt: string };

/** This page load's data URL for a live version (fetched once per version). */
export interface FreshLogo {
  tenantId: string;
  version: number;
  dataUrl: string;
}

export interface HeaderLogoInputs {
  tenantId: string;
  live: TenantBranding | undefined;
  cached: BrandingCacheEntry | null;
  fresh: FreshLogo | null;
  isError: boolean;
  fallbackName: string;
}

function liveLogoState(
  live: TenantBranding,
  { tenantId, cached, fresh }: HeaderLogoInputs,
  alt: string,
): HeaderLogoState {
  if (!isCustomLogoLive(live)) return { kind: 'wingo' };
  let src = live.logo_url;
  if (fresh && fresh.tenantId === tenantId && fresh.version === live.version) {
    src = fresh.dataUrl;
  } else if (cached && cached.version === live.version && cached.data_url) {
    src = cached.data_url;
  }
  return { kind: 'custom', src, badge: live.display_mode === 'light_badge', alt };
}

/**
 * What the header slot paints. The live answer wins; before it returns, the
 * cached answer paints at once; a cold cache is a neutral placeholder; no
 * tenant, a failed read or a cached non-logo answer is the Wingo logo.
 */
export function deriveHeaderLogo(inputs: HeaderLogoInputs): HeaderLogoState {
  const { tenantId, live, cached, isError } = inputs;
  const alt = `${live?.company_name || inputs.fallbackName || 'Company'} logo`;
  if (live) return liveLogoState(live, inputs, alt);
  if (cacheShowsLogo(cached) && cached?.data_url) {
    return {
      kind: 'custom',
      src: cached.data_url,
      badge: cached.display_mode === 'light_badge',
      alt,
    };
  }
  if (!tenantId || isError || (cached && !cacheShowsLogo(cached))) return { kind: 'wingo' };
  return { kind: 'pending' };
}

const warnedUncached = new Set<string>();

/**
 * The no-flash cache (§5.6) needs the bucket's CORS to allow this origin; a
 * failure would otherwise be silent (a placeholder on every reload).
 */
function warnUncachedOnce(tenantId: string, version: number, err: unknown): void {
  const key = `${tenantId}:v${version}`;
  if (warnedUncached.has(key)) return;
  warnedUncached.add(key);
  console.warn(
    `[branding] logo version ${version} could not be cached as a data URL ` +
      "(check the logo bucket's CORS for this origin); the header uses the signed URL.",
    err,
  );
}

/** Fetch a version's data URL into the cache; returns the effect's cancel. */
function fetchIntoCache(
  tenantId: string,
  index: BrandingCacheIndex,
  logoUrl: string,
  version: number,
  onFresh: (fresh: FreshLogo) => void,
): () => void {
  let cancelled = false;
  fetchLogoAsDataUrl(logoUrl)
    .then((dataUrl) => {
      if (cancelled) return;
      writeBrandingCache(tenantId, index, dataUrl);
      onFresh({ tenantId, version, dataUrl });
    })
    .catch((err: unknown) => {
      // No cache this load; the signed URL still renders. Say why, once per version.
      if (!cancelled) warnUncachedOnce(tenantId, version, err);
    });
  return () => {
    cancelled = true;
  };
}

/**
 * Record the live answer in the cache. A live logo reuses its cached data URL
 * when the version matches, else fetches it; returns the fetch's cancel.
 */
function syncCache(
  tenantId: string,
  index: BrandingCacheIndex,
  logoUrl: string | null,
  onFresh: (fresh: FreshLogo) => void,
): (() => void) | undefined {
  const { version } = index;
  if (!(index.entitled && index.kind === 'logo' && logoUrl && version !== null)) {
    writeBrandingCache(tenantId, index, null);
    return undefined;
  }
  const have = readBrandingCache(tenantId);
  if (have && have.version === version && have.data_url) {
    writeBrandingCache(tenantId, index, have.data_url);
    onFresh({ tenantId, version, dataUrl: have.data_url });
    return undefined;
  }
  writeBrandingCache(tenantId, index, null);
  return fetchIntoCache(tenantId, index, logoUrl, version, onFresh);
}

/** Keep the cache on the live answer; fetch the data URL once per version. */
function useBrandingCacheSync(
  tenantId: string,
  live: TenantBranding | undefined,
): FreshLogo | null {
  const [fresh, setFresh] = useState<FreshLogo | null>(null);
  const entitled = live?.entitled;
  const kind = live?.kind;
  const version = live?.version;
  const displayMode = live?.display_mode;
  const logoUrl = live?.logo_url;

  useEffect(() => {
    if (!tenantId || entitled === undefined || kind === undefined || displayMode === undefined) {
      return;
    }
    const index: BrandingCacheIndex = {
      entitled,
      kind,
      version: version ?? null,
      display_mode: displayMode,
    };
    return syncCache(tenantId, index, logoUrl ?? null, setFresh);
  }, [tenantId, entitled, kind, version, displayMode, logoUrl]);

  return fresh;
}

export function useHeaderLogo(): HeaderLogoState {
  const { currentTenant } = useTenantContext();
  const tenantId = useBrandingTenantId();
  const query = useTenantBranding();
  // Read once per tenant: the first paint of this page load.
  const cached = useMemo(() => readBrandingCache(tenantId), [tenantId]);
  const fresh = useBrandingCacheSync(tenantId, query.data);
  return deriveHeaderLogo({
    tenantId,
    live: query.data,
    cached,
    fresh,
    isError: query.isError,
    // An overridden currentTenant's name is not this tenant's.
    fallbackName: currentTenant.id === tenantId ? currentTenant.name : '',
  });
}

/** Whether a tenant logo is live, from the read or (before it returns) the cache. */
export function useCustomLogoLive(): boolean {
  const tenantId = useBrandingTenantId();
  const { data } = useTenantBranding();
  const cached = useMemo(() => readBrandingCache(tenantId), [tenantId]);
  if (data) return isCustomLogoLive(data);
  return cacheShowsLogo(cached);
}

function applyBranding(qc: QueryClient, tenantId: string, data: TenantBranding | undefined) {
  const key = tenantBrandingKeys.tenant(tenantId);
  if (data && typeof data.entitled === 'boolean') qc.setQueryData(key, data);
  void qc.invalidateQueries({ queryKey: key });
}

// A resent save after a lost response finds its token spent (a false "expired"),
// a resent restore appends a duplicate version, and every attempt spends the
// tenant's write budget: the writes are sent once.
export function useLogoPreview() {
  return useMutation({ mutationFn: (file: File) => previewLogo(file), retry: false });
}

export function useSaveLogo() {
  const qc = useQueryClient();
  const tenantId = useBrandingTenantId();
  return useMutation({
    mutationFn: (req: SaveLogoRequest) => saveLogo(req),
    retry: false,
    onSuccess: (data) => applyBranding(qc, tenantId, data),
  });
}

export function useRestoreBranding() {
  const qc = useQueryClient();
  const tenantId = useBrandingTenantId();
  return useMutation({
    mutationFn: (req: RestoreBrandingRequest) => restoreBranding(req),
    retry: false,
    onSuccess: (data) => applyBranding(qc, tenantId, data),
  });
}
